import { useCallback, useState } from 'react'
import { CalendarClock, ClipboardCopy, Link2, RefreshCcwDot, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import type { Revenue } from '@/services/modules/RevenuesService'
import {
  RevenueMaintenanceService,
  type AsaasUnlinkedCharge,
  type LinkableInvoice,
  type ResyncResult,
  type RevenueOwner,
  type StatementImportPreview,
} from '@/services/modules/RevenueMaintenanceService'

/**
 * Manutenção financeira de faturas, compartilhada entre a lista de faturas do
 * assinante e a da empresa — as regras precisam ser idênticas nos dois lugares.
 *
 *   const { toolbarAction, rowActions, dialogs } = useRevenueMaintenance(owner, refresh)
 *
 * Tudo aqui é da visão da administração: `useAuthority` já devolve `false` para
 * usuários EMPRESA e parceiros, então a tela de faturas da empresa não mostra
 * nada disso para o próprio cliente.
 *
 * Fatura com cobrança no Asaas não é editada nem excluída — o gateway é a fonte
 * da verdade dela. O backend recusa de novo, isto aqui é só a camada visual.
 */
export function useRevenueMaintenance(owner: RevenueOwner | null, refresh: () => void) {
  const { can } = useAuthority()
  const canSync = can('financial.revenues.sync_asaas')
  const canEditDates = can('financial.revenues.edit_dates')
  const canDelete = can('financial.revenues.delete')
  const canImportStatements = can('financial.revenues.import_statements')

  const [busy, setBusy] = useState(false)

  // -- Busca no Asaas --
  const [searchOpen, setSearchOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [charges, setCharges] = useState<AsaasUnlinkedCharge[]>([])
  const [linkable, setLinkable] = useState<LinkableInvoice[]>([])
  const [searchNote, setSearchNote] = useState<string | null>(null)
  /** paymentId → id da fatura local escolhida para receber o vínculo. */
  const [linkChoice, setLinkChoice] = useState<Record<string, string>>({})

  // -- Resincronização --
  const [resyncOpen, setResyncOpen] = useState(false)
  const [resyncing, setResyncing] = useState(false)
  const [resync, setResync] = useState<ResyncResult | null>(null)

  // -- Datas --
  const [datesTarget, setDatesTarget] = useState<Revenue | null>(null)
  const [dueDate, setDueDate] = useState('')
  const [paidDate, setPaidDate] = useState('')

  // -- Importar extrato de outro mês --
  const [importTarget, setImportTarget] = useState<Revenue | null>(null)
  const [importMonth, setImportMonth] = useState('')
  const [importYear, setImportYear] = useState('')
  const [importPreview, setImportPreview] = useState<StatementImportPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)

  // -- Exclusão --
  const [deleteTarget, setDeleteTarget] = useState<Revenue | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const runSearch = useCallback(async () => {
    if (!owner) return
    setSearching(true)
    setSearchNote(null)
    try {
      const res = await RevenueMaintenanceService.asaasUnlinked(owner)
      setCharges(res.charges)
      setLinkable(res.linkableInvoices)
      setSearchNote(res.reason ?? null)
      setLinkChoice({})
    } catch (e) {
      // Mesma razão do resync: uma falha na consulta não pode zerar o que já
      // estava na tela.
      toast.error({ title: 'Erro na busca', description: extractApiError(e).message })
    } finally {
      setSearching(false)
    }
  }, [owner])

  function openSearch() {
    setSearchOpen(true)
    void runSearch()
  }

  async function importCharge(charge: AsaasUnlinkedCharge, fromResync = false) {
    if (!owner) return
    setBusy(true)
    try {
      const created = await RevenueMaintenanceService.importCharge(owner, charge.paymentId)
      toast.success({ title: 'Fatura criada', description: `Fatura ${created.code} importada do Asaas. Comissões recalculadas.` })
      refresh()
      if (fromResync) {
        // Some da lista de sobras sem obrigar a rodar tudo de novo.
        setResync((prev) => prev && {
          ...prev,
          orphanCharges: prev.orphanCharges.filter((c) => c.paymentId !== charge.paymentId),
        })
      } else {
        void runSearch()
      }
    } catch (e) {
      toast.error({ title: 'Erro ao importar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function runResync() {
    if (!owner) return
    setResyncing(true)
    try {
      const res = await RevenueMaintenanceService.resync(owner)
      setResync(res)
      refresh()
    } catch (e) {
      // O painel NÃO é apagado: perder a lista de cobranças aqui foi o que
      // deixou o operador sem enxergar a fatura que faltava vincular.
      toast.error({ title: 'Erro na resincronização', description: extractApiError(e).message })
    } finally { setResyncing(false) }
  }

  function openResync() {
    setResync(null)
    setResyncOpen(true)
    void runResync()
  }

  async function linkCharge(charge: AsaasUnlinkedCharge) {
    const revenueId = linkChoice[charge.paymentId]
    if (!revenueId) return
    setBusy(true)
    try {
      const linked = await RevenueMaintenanceService.linkCharge(revenueId, charge.paymentId)
      toast.success({ title: 'Cobrança vinculada', description: `Fatura ${linked.code} atualizada com valor e datas do Asaas. Comissões recalculadas.` })
      refresh()
      void runSearch()
    } catch (e) {
      toast.error({ title: 'Erro ao vincular', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function submitDates() {
    if (!datesTarget) return
    setBusy(true)
    try {
      const recalculated = await RevenueMaintenanceService.updateDates(datesTarget.id, dueDate, paidDate || null)
      toast.success({ title: 'Datas atualizadas', description: describeRecalc(recalculated) })
      setDatesTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao salvar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function submitDelete() {
    if (!deleteTarget) return
    setBusy(true)
    try {
      const recalculated = await RevenueMaintenanceService.destroy(deleteTarget.id, deleteReason)
      toast.success({ title: 'Fatura excluída', description: describeRecalc(recalculated) })
      setDeleteConfirm(false); setDeleteTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao excluir', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  /** Abre o modal já sugerindo o mês anterior ao da fatura. */
  function openImport(r: Revenue) {
    const base = r.dueDate ? new Date(`${r.dueDate}T00:00:00`) : new Date()
    base.setMonth(base.getMonth() - 1)
    const month = String(base.getMonth() + 1)
    const year = String(base.getFullYear())

    setImportTarget(r)
    setImportMonth(month)
    setImportYear(year)
    setImportPreview(null)
    void runPreview(r, month, year)
  }

  async function runPreview(r: Revenue, month: string, year: string) {
    if (!month || !year) return
    setPreviewing(true)
    try {
      setImportPreview(await RevenueMaintenanceService.previewStatements(r.id, Number(month), Number(year)))
    } catch (e) {
      setImportPreview(null)
      toast.error({ title: 'Erro ao carregar a prévia', description: extractApiError(e).message })
    } finally { setPreviewing(false) }
  }

  function changeImportMonth(month: string) {
    setImportMonth(month)
    if (importTarget) void runPreview(importTarget, month, importYear)
  }

  function changeImportYear(year: string) {
    setImportYear(year)
    if (importTarget) void runPreview(importTarget, importMonth, year)
  }

  async function submitImport() {
    if (!importTarget) return
    setBusy(true)
    try {
      const res = await RevenueMaintenanceService.importStatements(importTarget.id, Number(importMonth), Number(importYear))
      toast.success({
        title: 'Extrato importado',
        description: `${res.imported} lançamento(s) copiado(s). ${describeRecalc(res.recalculated)}`,
      })
      setImportTarget(null); refresh()
    } catch (e) {
      toast.error({ title: 'Erro ao importar o extrato', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  function rowActions(r: Revenue): DropdownItem[] {
    const items: DropdownItem[] = []

    // Importar extrato não toca no gateway, então vale também para fatura do
    // Asaas — que é justamente o caso mais comum de fatura sem lançamentos.
    if (canImportStatements && r.statementsCount === 0) {
      items.push({
        key: 'import-statements', label: 'Copiar extrato de outro mês',
        icon: <ClipboardCopy size={14} />, divider: true,
        onClick: () => openImport(r),
      })
    }

    // As demais só para fatura sem cobrança no Asaas.
    if (isFromAsaas(r)) return items

    if (canEditDates) {
      items.push({
        key: 'edit-dates', label: 'Editar datas', icon: <CalendarClock size={14} />, divider: true,
        onClick: () => {
          setDatesTarget(r)
          setDueDate(r.dueDate ?? '')
          setPaidDate(r.paidDate ?? '')
        },
      })
    }
    if (canDelete) {
      items.push({
        key: 'delete-revenue', label: 'Excluir fatura', icon: <Trash2 size={14} />, danger: true,
        onClick: () => { setDeleteTarget(r); setDeleteReason('') },
      })
    }
    return items
  }

  const toolbarAction = canSync && owner
    ? (
      <>
        <Button variant="secondary" iconLeft={<RefreshCcwDot size={14} />} onClick={openResync}>Resincronizar Asaas</Button>
        <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={openSearch}>Buscar faturas no Asaas</Button>
      </>
    )
    : null

  const linkOptions = linkable.map((i) => ({
    value: i.id,
    label: `${i.code} — venc. ${formatDate(i.dueDate)} — ${formatMoney(i.amount)} (${i.status.toLowerCase()})`,
  }))

  const dialogs = (
    <>
      {/* Resincronização com o Asaas */}
      <Modal
        open={resyncOpen}
        onClose={() => setResyncOpen(false)}
        title="Resincronizar com o Asaas"
        size="lg"
        footer={<>
          <Button variant="ghost" onClick={() => setResyncOpen(false)} disabled={busy || resyncing}>Fechar</Button>
          <Button variant="secondary" iconLeft={<RefreshCcwDot size={14} />} onClick={() => void runResync()} loading={resyncing} disabled={busy}>Rodar de novo</Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Cada fatura é procurada no Asaas pelo id da cobrança e, na falta dele,
            por vencimento + valor. O que é encontrado tem id, forma de pagamento e
            pagamento atualizados; o que o Asaas não conhece passa a valer como
            fatura do sistema. Pagamento já registrado aqui nunca é desfeito.
          </p>

          {resyncing && <p className="text-sm text-gray-500">Consultando o Asaas…</p>}
          {!resyncing && resync?.reason && <p className="text-sm text-amber-600">{resync.reason}</p>}

          {!resyncing && resync && (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="gray">{resync.checked} fatura(s) verificada(s)</Badge>
                <Badge variant="blue">{resync.matched} no Asaas</Badge>
                <Badge variant="gray">{resync.unmatched} do sistema</Badge>
                <Badge variant={resync.updated > 0 ? 'green' : 'gray'}>{resync.updated} atualizada(s)</Badge>
                {resync.recalculatedInvoices > 0 && (
                  <Badge variant="violet">{resync.recalculatedInvoices} recalculada(s)</Badge>
                )}
              </div>

              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Fatura</th>
                      <th className="px-3 py-2 text-left font-medium">Vencimento</th>
                      <th className="px-3 py-2 text-right font-medium">Valor</th>
                      <th className="px-3 py-2 text-center font-medium">Origem</th>
                      <th className="px-3 py-2 text-left font-medium">Alterações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resync.rows.map((row) => (
                      <tr key={row.code} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">{row.code}</td>
                        <td className="px-3 py-2 text-gray-600">{formatDate(row.dueDate)}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{formatMoney(row.amount)}</td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant={row.result === 'asaas' ? 'blue' : 'gray'}>
                            {row.result === 'asaas' ? 'Asaas' : 'Sistema'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {row.changes.length > 0 ? row.changes.join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cobranças que existem no Asaas e não têm fatura aqui. */}
              {resync.orphanCharges.length > 0 && (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-xs font-medium text-amber-800">
                    {resync.orphanCharges.length} cobrança(s) no Asaas sem fatura no sistema
                  </p>
                  <p className="text-[11px] text-amber-700">
                    Nada foi criado automaticamente. Confira antes de trazer para o sistema.
                  </p>
                  {resync.orphanCharges.map((c) => (
                    <div key={c.paymentId} className="flex items-center justify-between gap-3 rounded-md bg-white p-2 ring-1 ring-amber-100">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-gray-800">
                          {formatMoney(c.value)} · venc. {formatDate(c.dueDate)} · {c.isPaid ? `pago ${formatDate(c.paymentDate)}` : 'em aberto'}
                        </div>
                        <div className="text-[11px] text-gray-500 break-words">
                          {c.description || 'Sem descrição'} · {c.billingType ?? '—'} · {c.paymentId}
                        </div>
                      </div>
                      <Button onClick={() => void importCharge(c, true)} disabled={busy}>Criar fatura</Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Busca de cobranças no Asaas */}
      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Faturas no Asaas fora do sistema"
        size="lg"
        footer={<>
          <Button variant="ghost" onClick={() => setSearchOpen(false)} disabled={busy}>Fechar</Button>
          <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={() => void runSearch()} loading={searching} disabled={busy}>Buscar de novo</Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Cobranças deste cliente no Asaas que não estão vinculadas a nenhuma fatura.
            Importe como fatura nova ou vincule a uma fatura existente sem cobrança —
            neste caso o valor e as datas passam a ser os do Asaas.
          </p>

          {searching && <p className="text-sm text-gray-500">Consultando o Asaas…</p>}
          {!searching && searchNote && <p className="text-sm text-amber-600">{searchNote}</p>}
          {!searching && !searchNote && charges.length === 0 && (
            <p className="text-sm text-gray-500">Nenhuma cobrança pendente de vínculo. Está tudo no sistema.</p>
          )}

          {!searching && charges.map((c) => (
            <div key={c.paymentId} className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800">
                    {formatMoney(c.value)} · venc. {formatDate(c.dueDate)}
                  </div>
                  <div className="text-[11px] text-gray-500 break-words">
                    {c.description || 'Sem descrição'} · {c.billingType ?? '—'} · {c.paymentId}
                  </div>
                </div>
                <Badge variant={c.isPaid ? 'green' : 'amber'}>{c.isPaid ? `Pago ${formatDate(c.paymentDate)}` : 'Em aberto'}</Badge>
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[240px] flex-1">
                  <Select
                    label="Vincular à fatura"
                    value={linkChoice[c.paymentId] ?? ''}
                    onChange={(e) => setLinkChoice((prev) => ({ ...prev, [c.paymentId]: e.target.value }))}
                    options={linkOptions}
                    placeholder="Selecione a fatura do sistema"
                    searchPlaceholder="Buscar fatura"
                    hint={linkOptions.length === 0 ? 'Nenhuma fatura sem cobrança para vincular.' : undefined}
                  />
                </div>
                <Button
                  variant="secondary"
                  iconLeft={<Link2 size={14} />}
                  onClick={() => void linkCharge(c)}
                  disabled={busy || !linkChoice[c.paymentId]}
                >
                  Vincular
                </Button>
                <Button onClick={() => void importCharge(c)} disabled={busy}>Criar fatura</Button>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Copiar extrato de outro mês */}
      <Modal
        open={!!importTarget}
        onClose={() => setImportTarget(null)}
        title={`Copiar extrato — fatura ${importTarget?.code ?? ''}`}
        size="lg"
        footer={<>
          <Button variant="ghost" onClick={() => setImportTarget(null)} disabled={busy}>Cancelar</Button>
          <Button
            onClick={() => void submitImport()}
            loading={busy}
            disabled={previewing || !importPreview?.items.length}
          >
            Copiar extrato
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Esta fatura está sem lançamentos. O extrato de outro mês serve de molde:
            quem entrou depois desta competência, ou saiu antes dela, não é copiado.
            O valor da fatura não é alterado.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Mês de origem" required
              value={importMonth} onChange={(e) => changeImportMonth(e.target.value)}
              options={MONTH_OPTIONS} placeholder="Selecione o mês" clearable={false}
            />
            <Select
              label="Ano de origem" required
              value={importYear} onChange={(e) => changeImportYear(e.target.value)}
              options={yearOptions()} placeholder="Selecione o ano" clearable={false}
            />
          </div>

          {previewing && <p className="text-sm text-gray-500">Carregando a prévia…</p>}

          {!previewing && importPreview && !importPreview.source && (
            <p className="text-sm text-amber-600">Nenhuma fatura deste cliente com extrato neste mês.</p>
          )}

          {!previewing && importPreview?.source && (
            <>
              <p className="text-xs text-gray-500">
                Origem: fatura {importPreview.source.code} — venc. {formatDate(importPreview.source.dueDate)}
              </p>

              {importPreview.dropped.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-xs font-medium text-amber-800">
                    {importPreview.dropped.length} não se aplica(m) a esta competência:
                  </p>
                  <ul className="mt-1 space-y-0.5 text-[11px] text-amber-700">
                    {importPreview.dropped.map((d) => <li key={d.name}>{d.name} — {d.reason}</li>)}
                  </ul>
                </div>
              )}

              {importPreview.missing.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-xs font-medium text-amber-800">
                    {importPreview.missing.length} colaborador(es) valiam nesta competência e não estão no extrato de origem:
                  </p>
                  <ul className="mt-1 space-y-0.5 text-[11px] text-amber-700">
                    {importPreview.missing.map((m) => <li key={m.name}>{m.name} — entrou em {formatDate(m.joinedAt)}</li>)}
                  </ul>
                  <p className="mt-1 text-[11px] text-amber-700">
                    Não serão copiados: não há plano/desconto daquele mês. Adicione manualmente depois.
                  </p>
                </div>
              )}

              <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Colaborador</th>
                      <th className="px-3 py-2 text-right font-medium">Plano</th>
                      <th className="px-3 py-2 text-right font-medium">Desconto</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.items.map((i, idx) => (
                      <tr key={`${i.subscriberId ?? i.name}-${idx}`} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">{i.name}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatMoney(i.planAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatMoney(i.discount)}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-800">{formatMoney(i.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Soma a copiar</span>
                <span className="font-medium text-gray-800">{formatMoney(importPreview.total)}</span>
              </div>
              {Math.abs(importPreview.total - importPreview.targetAmount) >= 0.01 && (
                <p className="text-xs text-amber-600">
                  A soma difere do valor da fatura ({formatMoney(importPreview.targetAmount)}). O valor
                  não será alterado — confira a composição depois de copiar.
                </p>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Editar datas */}
      <Modal
        open={!!datesTarget}
        onClose={() => setDatesTarget(null)}
        title={`Editar datas — fatura ${datesTarget?.code ?? ''}`}
        size="sm"
        footer={<>
          <Button variant="ghost" onClick={() => setDatesTarget(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={() => void submitDates()} loading={busy} disabled={!dueDate}>Salvar</Button>
        </>}
      >
        <div className="space-y-4">
          <Input
            label="Vencimento" type="date" required
            value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            placeholder="dd/mm/aaaa"
          />
          {datesTarget?.status === 'PAGO' && (
            <Input
              label="Data do pagamento" type="date" required
              value={paidDate} onChange={(e) => setPaidDate(e.target.value)}
              placeholder="dd/mm/aaaa"
            />
          )}
          <p className="text-xs text-gray-500">
            Mudar o vencimento reordena as parcelas do cliente, então as comissões
            das outras faturas pagas também são recalculadas.
          </p>
        </div>
      </Modal>

      {/* Excluir */}
      <Modal
        open={!!deleteTarget && !deleteConfirm}
        onClose={() => setDeleteTarget(null)}
        title={`Excluir fatura ${deleteTarget?.code ?? ''}`}
        size="sm"
        footer={<>
          <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={busy}>Cancelar</Button>
          <Button variant="danger" onClick={() => setDeleteConfirm(true)} disabled={busy}>Continuar</Button>
        </>}
      >
        <div className="space-y-4">
          <Input
            label="Motivo da exclusão"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Ex.: fatura lançada em duplicidade"
            hint="Fica registrado junto com quem excluiu e quando."
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={() => void submitDelete()}
        loading={busy}
        variant="danger"
        title="Excluir esta fatura?"
        description={`A fatura ${deleteTarget?.code ?? ''} sai das listagens e dos relatórios, e as comissões dela são removidas. O registro fica guardado para auditoria.`}
        confirmLabel="Excluir fatura"
      />
    </>
  )

  return { toolbarAction, rowActions, dialogs, canSync, canEditDates, canDelete }
}

/**
 * Fatura controlada pelo Asaas. O `code` iniciado por `pay_` cobre as faturas
 * importadas do sistema antigo, que guardaram o id da cobrança ali em vez do
 * `external_id` — sem essa checagem elas apareceriam como editáveis.
 */
function isFromAsaas(r: Revenue): boolean {
  return !!r.externalId || (r.code ?? '').startsWith('pay_')
}

function describeRecalc(count: number): string {
  if (count <= 0) return 'Nenhuma comissão precisou ser recalculada.'
  return count === 1
    ? 'Comissões recalculadas em 1 fatura.'
    : `Comissões recalculadas em ${count} faturas do cliente.`
}

const MONTH_OPTIONS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
].map((label, i) => ({ value: String(i + 1), label }))

/** Ano corrente e os quatro anteriores — cobre a janela real de correção. */
function yearOptions() {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => String(current - i)).map((y) => ({ value: y, label: y }))
}

function formatMoney(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const [y, m, d] = value.slice(0, 10).split('-')
  return y && m && d ? `${d}/${m}/${y}` : value
}
